import React, { useState, useEffect } from 'react';
import { searchSummary } from '../common/form/MobileFilterCollapse';
import SearchCard, { fieldProps } from '../common/list/SearchCard';
import { Box, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import 'dayjs/locale/ko';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MobileSearchAdornment from '../common/search/MobileSearchAdornment';
import {
  firstDayofYear,
  getFirstDayOfYear,
  getLastDayOfYear,
  lastDayofYear,
  subYearStrDt,
} from '../../utils/calcDatetime';

// 영업 진행 현황(/chancemng) 검색 영역 - 공통 SearchCard 사용(2026-10-22).
// 그리드 칸(gridTemplateAreas/Columns)만 이 화면 고유 - 나머지(카드/버튼/모바일 접기)는
// components/common/list/SearchCard.js 가 담당한다.
export const ChanceListBox = ({ formik, onReset }) => {
  const [searchStrtDt, setSearchStrtDt] = useState(null);
  const [searchEndDt, setSearchEndDt] = useState(null);
  useEffect(() => {
    if (formik.values.searchStrtDt) {
      setSearchStrtDt(dayjs(formik.values.searchStrtDt));
    }
    if (formik.values.searchEndDt) {
      setSearchEndDt(dayjs(formik.values.searchEndDt));
    }
  }, [formik.values.searchStrtDt, formik.values.searchEndDt]);

  const handleChangeThisYear = () => {
    formik.setFieldValue('searchStrtDt', firstDayofYear);
    formik.setFieldValue('searchEndDt', lastDayofYear);
  };

  const handleChangeLastYear = () => {
    const paramDate = subYearStrDt(1);
    const strDt = getFirstDayOfYear(paramDate);
    const endDt = getLastDayOfYear(paramDate);

    formik.setFieldValue('searchStrtDt', strDt);
    formik.setFieldValue('searchEndDt', endDt);
  };

  return (
    <SearchCard
      summary={searchSummary(formik.values)}
      onSubmit={formik.handleSubmit}
      onReset={onReset}
      submitting={formik.isSubmitting}
      gridSx={{
        // 2026-10-22 재조정: 검색기준(select)과 검색어(word)를 각각 독립된 grid 칸으로
        // 쪼갰다 - 한 칸 안에 플렉스로 같이 넣어두니 검색어가 날짜칸을 넓힌 대가로
        // 70px까지 눌렸다(코디네이터 확인). "검색어=가변(1fr, 최소~150px)"이 되려면
        // 그리드 트랙 자체가 가변이어야 한다. 1200~1439px는 전부 한 줄에 못 들어가서
        // (바닥값 합이 가용폭을 넘음, 아래 각 구간 주석 참고) 검색어+버튼을 둘째 줄로
        // 내린다. 1440px부터 진짜 한 줄.
        gridTemplateAreas: {
          xs: `"period" "dept" "user" "keyword" "search" "btns"`,
          // col1(큰 칸)을 period/search가 같이 쓰고, col3(버튼 폭)을 user/btns가 같이 쓴다 -
          // 두 줄이 같은 3칸을 쓰는 그리드라 열 폭은 공유되지만 어느 줄에 뭐가 들어갈지는
          // area 이름으로 자유롭게 배치 가능(2026-10-22, 검색어를 넓은 칸에 배치).
          sm: `"period dept user" "search keyword btns"`,
        },
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'minmax(200px, 1.1fr) minmax(110px, 0.8fr) minmax(190px, 0.9fr)',
        },
        '@media (min-width:1200px)': {
          // 1200~1439px: period+dept+user+keyword 바닥값 합만으로도 710px 안팎이라
          // 1280px(카드 안쪽 ~958px)에서 검색어(최소 150)+버튼(196)까지 한 줄에 못 넣는다
          // (실측: 710+150+196+5*12gap=1116 > 958). 검색어+버튼을 둘째 줄로.
          gridTemplateAreas: `"period dept user keyword" "search search btns btns"`,
          gridTemplateColumns:
            'minmax(300px, 1.4fr) minmax(100px, 0.7fr) minmax(100px, 0.7fr) minmax(110px, 0.7fr)',
        },
        '@media (min-width:1440px)': {
          // 1440px부터: 카드 안쪽이 넉넉해져(~1118px+) 한 줄에 다 들어간다. btns는 고정폭
          // (190px) - 'auto'로 두면 그리드가 필요한 만큼 그냥 가져가 버려 바닥값만으로 이미
          // 꽉 찬 폭에서 검색 버튼이 카드 밖으로 밀려났던 적이 있다(2026-10-22 실측 버그).
          gridTemplateAreas: `"period dept user keyword search btns"`,
          gridTemplateColumns:
            'minmax(360px, 1.6fr) minmax(100px, 0.6fr) minmax(100px, 0.6fr) minmax(110px, 0.6fr) minmax(150px, 1fr) 190px',
        },
      }}
    >
      {/* 기간: 금년/작년 토글 + 시작~종료 */}
      <Box
        sx={{
          gridArea: 'period',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <ToggleButtonGroup size="small" exclusive sx={{ flexShrink: 0, flexWrap: 'nowrap' }}>
          <ToggleButton
            value="thisYear"
            onClick={handleChangeThisYear}
            sx={{ textTransform: 'none', px: 1.25 }}
          >
            금년
          </ToggleButton>
          <ToggleButton
            value="lastYear"
            onClick={handleChangeLastYear}
            sx={{ textTransform: 'none', px: 1.25 }}
          >
            작년
          </ToggleButton>
        </ToggleButtonGroup>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
            <DatePicker
              label="시작일"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              id="searchStrtDt"
              name="searchStrtDt"
              value={searchStrtDt}
              onChange={(newValue) => {
                setSearchStrtDt(dayjs(newValue));
                formik.setFieldValue('searchStrtDt', dayjs(newValue));
              }}
              slotProps={{
                textField: {
                  ...fieldProps,
                  sx: { minWidth: 0, flex: '1 1 130px' },
                  error: formik.touched.searchStrtDt && Boolean(formik.errors.searchStrtDt),
                  helperText: formik.touched.searchStrtDt && formik.errors.searchStrtDt,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              ~
            </Typography>
            <DatePicker
              label="종료일"
              format="YYYY-MM-DD"
              mask={'____-__-__'}
              id="searchEndDt"
              name="searchEndDt"
              value={searchEndDt}
              onChange={(newValue) => {
                setSearchEndDt(dayjs(newValue));
                formik.setFieldValue('searchEndDt', dayjs(newValue));
              }}
              slotProps={{
                textField: {
                  ...fieldProps,
                  sx: { minWidth: 0, flex: '1 1 130px' },
                  error: formik.touched.searchEndDt && Boolean(formik.errors.searchEndDt),
                  helperText: formik.touched.searchEndDt && formik.errors.searchEndDt,
                },
              }}
            />
          </Box>
        </LocalizationProvider>
      </Box>

      {/* 영업부서 */}
      <Box sx={{ gridArea: 'dept', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          fullWidth
          required={false}
          id="searchDpt"
          name="searchDpt"
          label="영업부서"
          value={formik.values.searchDpt}
          onChange={formik.handleChange}
          error={formik.touched.searcDpt && Boolean(formik.errors.searcDpt)}
          helperText={formik.touched.searcDpt && formik.errors.searcDpt}
        />
      </Box>

      {/* 영업사원 */}
      <Box sx={{ gridArea: 'user', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          fullWidth
          required={false}
          id="searchUsr"
          name="searchUsr"
          label="영업사원"
          value={formik.values.searchUsr}
          onChange={formik.handleChange}
          error={formik.touched.searchUsr && Boolean(formik.errors.searchUsr)}
          helperText={formik.touched.searchUsr && formik.errors.searchUsr}
        />
      </Box>

      {/* 검색 기준 (select) - 독립 grid 칸(2026-10-22, 검색어와 분리) */}
      <Box sx={{ gridArea: 'keyword', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          select
          fullWidth
          required={false}
          id="searchKeyword"
          name="searchKeyword"
          label="검색 기준"
          value={formik.values.searchKeyword}
          onChange={formik.handleChange}
          error={formik.touched.searchKeyword && Boolean(formik.errors.searchKeyword)}
          helperText={formik.touched.searchKeyword && formik.errors.searchKeyword}
        >
          <MenuItem value="cstNm">사업명</MenuItem>
          <MenuItem value="prjNo">프로젝트 번호</MenuItem>
          <MenuItem value="vendorCmpNm">매출처명</MenuItem>
          <MenuItem value="crtDptNm">부서</MenuItem>
          <MenuItem value="crtNm">영업담당자</MenuItem>
          <MenuItem value="cstStatus">상태</MenuItem>
        </TextField>
      </Box>

      {/* 검색어 - 독립 grid 칸, 1fr로 가변(2026-10-22) */}
      <Box sx={{ gridArea: 'search', minWidth: 0 }}>
        <TextField
          {...fieldProps}
          fullWidth
          required={false}
          id="searchWords"
          name="searchWords"
          label="검색어"
          placeholder="검색어 입력"
          value={formik.values.searchWords}
          onChange={formik.handleChange}
          error={formik.touched.searchWords && Boolean(formik.errors.searchWords)}
          helperText={formik.touched.searchWords && formik.errors.searchWords}
          InputProps={{
            startAdornment: (
              <SearchIcon
                fontSize="small"
                sx={{ color: 'text.disabled', mr: 0.5, display: { xs: 'none', sm: 'inline-flex' } }}
              />
            ),
            endAdornment: <MobileSearchAdornment disabled={formik.isSubmitting} />,
          }}
        />
      </Box>
    </SearchCard>
  );
};
